import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { AfterViewInit, Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { CORE_MOMENT_DATA_SERVICE, MomentDataService } from '@app/app/page-modules/folio/shared-moment-module/services/data.service';
import { COMMENT_DATA_SERVICE, CommentDataService } from '@app/app/shared/services/comment/comment-data.service';
import { TranslocoService } from '@ngneat/transloco';
import { Select } from '@ngxs/store';
import { Observable, Subject, debounceTime, finalize, takeUntil } from 'rxjs';
import { UserDetailsSummary } from '../../../user-auth/models';
import { UserAuthState } from '../../../user-auth/store/user-auth.state';
import { Comment, CommentSortOption, UiComment } from '../../models/comment/comment.model';

export interface V3MomentCardCommentsDialogData {
  momentId: string;
  initialCommentsCount?: number;
}

@Component({
  selector: 'ptl-v3-moment-card-comments',
  templateUrl: './v3-moment-card-comments.component.html',
  styleUrls: ['./v3-moment-card-comments.component.scss'],
})
export class V3MomentCardCommentsComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() momentId!: string;
  @Output() commentCountUpdate = new EventEmitter<number>();

  @ViewChild(CdkVirtualScrollViewport) viewport!: CdkVirtualScrollViewport;

  @Select(UserAuthState.userDetailsData)
  userDetails$: Observable<UserDetailsSummary>;

  comments: UiComment[] = [];
  totalCommentsCount = 0;
  isLoading = false;
  hasMore = false;
  currentPage = 0;
  currentSort: CommentSortOption['value'] = 'NEWEST';
  dialogTitle = 'Comments';
  currentUserId: string | null = null;

  readonly sortOptions: CommentSortOption[] = [
    { value: 'NEWEST', label: this.translocoService.translate('translations.comments.sort.newest') },
    { value: 'OLDEST', label: this.translocoService.translate('translations.comments.sort.oldest') },
    { value: 'MOST_LIKED', label: this.translocoService.translate('translations.comments.sort.mostLiked') },
    { value: 'MOST_REPLIED', label: this.translocoService.translate('translations.comments.sort.mostReplied') },
  ];

  private destroy$ = new Subject<void>();

  constructor(
    public translocoService: TranslocoService,
    @Inject(MAT_DIALOG_DATA) public dialogData: V3MomentCardCommentsDialogData,
    @Inject(COMMENT_DATA_SERVICE) private commentService: CommentDataService,
    @Inject(CORE_MOMENT_DATA_SERVICE) private momentService: MomentDataService,
    public dialogRef: MatDialogRef<V3MomentCardCommentsComponent>,
  ) {}

  ngOnInit(): void {
    this.momentId = this.dialogData.momentId;
    this.totalCommentsCount = this.dialogData.initialCommentsCount || 0;

    this.userDetails$.subscribe((userDetails) => {
      if (userDetails) {
        this.currentUserId = userDetails.uid;
      }
    });

    this.loadComments();
  }

  ngAfterViewInit(): void {
    if (this.viewport) {
      this.viewport.scrolledIndexChange.pipe(takeUntil(this.destroy$), debounceTime(300)).subscribe(() => {
        this.onScroll();
      });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadComments(reset: boolean = false): void {
    if (this.isLoading) return;

    this.isLoading = true;

    if (reset) {
      this.currentPage = 0;
      this.comments = [];
    }

    this.commentService
      .getComments(this.momentId, this.currentPage, 15, this.currentSort)
      .pipe(
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe({
        next: (result) => {
          if (result.isSuccess) {
            const newComments = this.toUiComments(result.value.content);

            if (reset) {
              this.comments = newComments;
            } else {
              this.comments = [...this.comments, ...newComments];
            }

            this.hasMore = result.value.currentPageNumber < result.value.numberOfPages;
            this.currentPage++;
          } else {
            console.error('Error loading comments:', result.error);
          }
        },
        error: (error) => {
          console.error('Error loading comments:', error);
        },
      });
  }

  onScroll(): void {
    if (!this.viewport || this.isLoading || !this.hasMore) return;

    const end = this.viewport.getRenderedRange().end;
    const total = this.viewport.getDataLength();

    if (end >= total - 2) {
      this.loadComments();
    }
  }

  onSortChange(sort: CommentSortOption['value']): void {
    if (this.currentSort === sort) return;

    this.currentSort = sort;
    this.loadComments(true);
  }

  onCommentCreated(comment: Comment): void {
    const uiComment = this.toUiComment(comment);
    this.comments = [uiComment, ...this.comments];
    this.refreshMomentDetails();
  }

  onCommentUpdated(updatedComment: Comment): void {
    this.comments = this.updateCommentRecursively(this.comments, updatedComment);
  }

  private updateCommentRecursively(comments: UiComment[], updatedComment: Comment): UiComment[] {
    return comments.map((comment) => {
      if (comment.id === updatedComment.id) {
        return {
          ...comment,
          content: updatedComment.content,
          updatedAt: updatedComment.updatedAt,
        };
      }

      if (comment.replies && comment.replies.length > 0) {
        return {
          ...comment,
          replies: this.updateCommentRecursively(comment.replies as UiComment[], updatedComment),
        };
      }

      return comment;
    });
  }

  onCommentDeleted(commentId: string): void {
    this.comments = this.deleteCommentRecursively(this.comments, commentId);
    this.refreshMomentDetails();
  }

  private deleteCommentRecursively(comments: UiComment[], commentId: string): UiComment[] {
    return comments
      .filter((comment) => comment.id !== commentId)
      .map((comment) => {
        if (comment.replies && comment.replies.length > 0) {
          const updatedReplies = this.deleteCommentRecursively(comment.replies as UiComment[], commentId);
          return {
            ...comment,
            replies: updatedReplies,
            repliesCount: updatedReplies.length,
          };
        }
        return comment;
      });
  }

  onCommentLiked(commentId: string, isLiked: boolean): void {
    const comment = this.findCommentById(commentId, this.comments);
    if (comment) {
      comment.likes.liked = isLiked;
      comment.likes.totalLikes += isLiked ? 1 : -1;
    }
  }

  private findCommentById(commentId: string, comments: Comment[]): Comment | null {
    for (const comment of comments) {
      if (comment.id === commentId) {
        return comment;
      }
      if (comment.replies && comment.replies.length > 0) {
        const found = this.findCommentById(commentId, comment.replies);
        if (found) {
          return found;
        }
      }
    }
    return null;
  }

  onReplyCreated(reply: Comment, parentCommentId: string): void {
    const uiReply = this.toUiComment(reply);
    this.comments = this.updateCommentWithReply(this.comments, parentCommentId, uiReply);
    this.refreshMomentDetails();
  }

  private updateCommentWithReply(comments: UiComment[], parentCommentId: string, newReply: UiComment): UiComment[] {
    return comments.map((comment) => {
      if (comment.id === parentCommentId) {
        const currentReplies = comment.replies || [];
        return {
          ...comment,
          replies: [...currentReplies, newReply],
          repliesCount: currentReplies.length + 1,
          isExpanded: true, // Automatically expand to show the new reply
        };
      }

      if (comment.replies && comment.replies.length > 0) {
        return {
          ...comment,
          replies: this.updateCommentWithReply(comment.replies as UiComment[], parentCommentId, newReply),
        };
      }

      return comment;
    });
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  toUiComment(comment: Comment): UiComment {
    return this.convertToUiComment(comment);
  }

  toUiComments(comments: Comment[]): UiComment[] {
    return comments.map((comment) => this.convertToUiComment(comment));
  }

  private convertToUiComment(comment: Comment): UiComment {
    return {
      ...comment,
      isExpanded: false,
      isEditing: false,
      isReplying: false,
      replies: comment.replies ? comment.replies.map((reply) => this.convertToUiComment(reply)) : undefined,
    };
  }

  private refreshMomentDetails(): void {
    this.momentService.getMomentDetails(this.momentId).subscribe({
      next: (result) => {
        if (result.isSuccess && result.value) {
          this.totalCommentsCount = result.value.commentsCount || 0;
          this.commentCountUpdate.emit(this.totalCommentsCount);
        }
      },
      error: (error) => {
        console.error('Error refreshing moment details:', error);
      },
    });
  }
}
